import { Progress } from "@mantine/core";
import type { ReactNode } from "react";
import type { Campaign } from "../api/types";
import { STAGE_WORDS } from "../lib/campaign";
import { expectedWords } from "../lib/enrichment";
import { recentDate, shortDate } from "../lib/format";
import { Button } from "../ui/Button";
import { IconCheck } from "../ui/icons";
import { Spinner } from "./Spinner";

/* A campaign's stages as a flow.
 *
 * Five steps read left to right as chevrons: Planning, Enriching, Ready to
 * call, Calling and Done. A finished step has a check, the current one is
 * filled with its date under its name, and the steps ahead are pale. Done
 * takes one of two forms, Completed or Abandoned, chosen when the campaign
 * gets there, and once there the step shows its form (James, 2026-10-09).
 * Each step the engine lists in api.campaign.stage_moves (migrations 208,
 * 209 and 256) is a button that opens the move dialog. From Planning those
 * are Enriching, which is Start enrichment, and Ready to call.
 *
 * On a phone the steps are rows. Every fact here is api.campaign's.
 * docs/plans/campaigns-overhaul.md. */
const FLOW = ["draft", "enriching", "ready_to_call", "calling", "done"] as const;
const DONE_FORMS = ["completed", "abandoned"];

export function StageFlow({ c, onMove }: { c: Campaign; onMove: ((to: string) => void) | null }) {
  const stage = c.stage ?? "draft";
  const ended = DONE_FORMS.includes(stage);
  const at = ended ? FLOW.length - 1 : FLOW.indexOf(stage as (typeof FLOW)[number]);
  const moves = new Set(onMove ? (c.stage_moves ?? []) : []);
  const movable = (s: string) => (s === "done" ? DONE_FORMS.some((f) => moves.has(f)) : moves.has(s));
  const word = (s: string) => (s === "done" ? (ended ? STAGE_WORDS[stage] : "Done") : STAGE_WORDS[s]) ?? s;

  return (
    <section className="sf" aria-label="Stage">
      <ol className="sf-steps">
        {FLOW.map((s, i) => {
          const state = i < at ? "past" : i === at ? "now" : "ahead";
          const inner = (
            <>
              <span className="sf-shape" aria-hidden="true" />
              <span className="sf-word">
                {state === "past" ? <IconCheck /> : null}
                <span>{word(s)}</span>
              </span>
              {state === "now" && c.stage_at ? <span className="sf-when">since {recentDate(c.stage_at)}</span> : null}
            </>
          );
          return (
            <li key={s} className={`sf-step sf-${state}${s === "done" && stage === "abandoned" ? " sf-abandoned" : ""}`}
                aria-current={state === "now" ? "step" : undefined}>
              {movable(s) && onMove ? (
                <button type="button" className="sf-hit" onClick={() => onMove(s)}
                        aria-label={s === "done" ? (ended ? "Change how it ended" : "Move to Done") : `Move to ${word(s)}`}>
                  {inner}
                </button>
              ) : <div className="sf-hit">{inner}</div>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** The next step, in the header under the facts: the one action that moves
 *  the campaign on, at the left, with the engine's line beside it, then the
 *  work running for it and a refused step's reason. A heavy button belongs
 *  at the start of its row, where the eye begins, and not out in the middle
 *  of the page (James, 2026-10-09). With no action, the line alone.
 *  Under it, the engine's sentence about the calling date, in the refusal
 *  color when the date has come and the campaign is not ready, or its web
 *  lookups will finish after it (engine migration 256). */
export function NextStep({ c, onStep, error, exportLine, onUpdateList, updating }: {
  c: Campaign;
  onStep: ((step: string) => void) | null;
  error?: string | null;
  exportLine?: ReactNode;
  /** Update the list, offered when the engine says the criteria changed
   *  after the list was made (api.campaign.list_behind, migration 296). */
  onUpdateList?: (() => void) | null;
  updating?: boolean;
}) {
  return (
    <div className="cp-next">
      <div className="cp-next-row">
        {c.next_step && c.next_step_label && onStep ? (
          <Button kind="orange" onClick={() => onStep(c.next_step as string)}>{c.next_step_label}</Button>
        ) : null}
        {c.where_it_stands ? <p className="cp-next-line">{c.where_it_stands}</p> : null}
      </div>
      {c.calling_words ? (
        <p className={c.calling_warning ? "refusal cp-calling" : "cp-calling"}>{c.calling_words}</p>
      ) : null}
      {/* Not orange: the next step's button is the page's one orange action. */}
      {c.list_behind && c.list_behind_words ? (
        <div className="cp-next-row">
          {onUpdateList ? <Button onClick={onUpdateList} disabled={Boolean(updating)}>Update the list</Button> : null}
          <p className="cp-next-line">{c.list_behind_words}</p>
        </div>
      ) : null}
      <Running c={c} />
      {exportLine}
      {error ? <p className="refusal" role="alert">{error}</p> : null}
    </div>
  );
}

/** One bar per piece of work running for the campaign, with the engine's
 *  expected finish where it has one. Nothing when nothing runs. A file being
 *  made is CampaignFile's line, which NextStep draws under these, so the
 *  page says it once (engine migration 293). */
export function Running({ c }: { c: Campaign }) {
  const rows: ReactNode[] = [];
  if (c.list_job_status === "queued" || c.list_job_status === "running") {
    rows.push(<Bar key="list" label="Updating the list" />);
  }
  if ((c.web_waiting ?? 0) > 0 && c.web_members) {
    const done = c.web_members - (c.web_waiting ?? 0);
    rows.push(
      <Bar key="web" label="Web enrichment" value={(100 * done) / c.web_members}
           says={`${done.toLocaleString()} of ${c.web_members.toLocaleString()}, ${expectedWords(c.web_expected_done_at)}`} />,
    );
  } else if (c.enrich_job_status === "queued" || c.enrich_job_status === "running") {
    rows.push(<Bar key="search" label="Searching the web for new records" />);
  }
  if ((c.apollo_open ?? 0) > 0) rows.push(<Bar key="apollo" label="Apollo enrichment" />);
  /* A failed automatic run says so, with its reason, whatever the stage.
     Until migration 204 it read as nothing running. */
  if (c.enrich_job_error) {
    rows.push(
      <p key="failed" className="refusal cp-failed" role="alert">
        Automatic web enrichment failed{c.enrich_job_finished_at ? <> {shortDate(c.enrich_job_finished_at)}</> : null}. {c.enrich_job_error}
      </p>,
    );
  }
  if (!rows.length) return null;
  return <div className="cp-running">{rows}</div>;
}

function Bar({ label, value, says }: { label: string; value?: number; says?: string }) {
  return (
    <div className="cp-bar">
      <span className="cp-bar-label">{label}</span>
      {value === undefined
        ? <span className="cp-bar-spin"><Spinner /></span>
        : <Progress value={value} size="sm" className="cp-bar-meter" aria-label={label} />}
      {says ? <span className="cp-bar-says muted">{says}</span> : null}
    </div>
  );
}
